
import { Brain } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <footer className="py-12 px-4 md:px-8 bg-gradient-to-br from-brain-soft-purple/30 to-white border-t border-brain-light-purple/30 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute w-64 h-64 rounded-full bg-brain-purple/5 -bottom-32 -left-32"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div 
          className="absolute w-48 h-48 rounded-full bg-brain-teal/5 top-10 right-10"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 0.5
          }}
        />
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="absolute inset-0 bg-brain-purple/20 rounded-full blur-md"></div>
                <Brain size={28} className="text-brain-purple relative z-10" />
              </div>
              <h2 className="text-xl font-bold bg-gradient-to-r from-brain-purple to-brain-teal bg-clip-text text-transparent">
                BrainWave
              </h2>
            </div>
            
            <p className="text-sm md:text-base text-muted-foreground max-w-xs">
              Train your brain daily with our science-backed cognitive games designed to improve memory, focus, and problem-solving skills.
            </p>
            
            <div className="flex items-center gap-3 pt-2">
              <motion.button 
                className="w-8 h-8 rounded-full bg-brain-purple/10 flex items-center justify-center text-brain-purple"
                whileHover={{ scale: 1.1, backgroundColor: "rgba(155, 135, 245, 0.2)" }}
                onClick={() => handleExternalLink("https://twitter.com/brainwave")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
              </motion.button>
              <motion.button 
                className="w-8 h-8 rounded-full bg-brain-purple/10 flex items-center justify-center text-brain-purple"
                whileHover={{ scale: 1.1, backgroundColor: "rgba(155, 135, 245, 0.2)" }}
                onClick={() => handleExternalLink("https://facebook.com/brainwave")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
              </motion.button>
              <motion.button 
                className="w-8 h-8 rounded-full bg-brain-purple/10 flex items-center justify-center text-brain-purple"
                whileHover={{ scale: 1.1, backgroundColor: "rgba(155, 135, 245, 0.2)" }}
                onClick={() => handleExternalLink("https://instagram.com/brainwave")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
              </motion.button>
            </div>
          </div>
          
          <div>
            <h3 className="font-medium mb-4 text-brain-purple">Games</h3>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <Link to="/games?category=memory" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Memory Games
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/games?category=focus" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Focus Games
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/games?category=problem-solving" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Problem Solving
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/games" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  All Games
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium mb-4 text-brain-purple">Resources</h3>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <Link to="/about#brain-science" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Brain Science
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/about#training-tips" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Training Tips
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/about#research" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Research
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Blog
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium mb-4 text-brain-purple">Company</h3>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <Link to="/about" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  About Us
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Contact
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Privacy
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-brain-purple transition-colors inline-flex relative group">
                  Terms
                  <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-brain-purple transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        <motion.div 
          className="pt-8 border-t border-brain-light-purple/20 flex flex-col md:flex-row justify-between items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-sm text-muted-foreground">
            &copy; {currentYear} BrainWave. All rights reserved.
          </p>
          
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-muted-foreground hover:text-brain-purple transition-colors text-sm">
              Terms
            </Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-brain-purple transition-colors text-sm">
              Privacy
            </Link>
            <Link to="/cookies" className="text-muted-foreground hover:text-brain-purple transition-colors text-sm">
              Cookies
            </Link>
          </div>
          
          <p className="text-sm text-brain-purple/70 italic">
            "Train your brain daily for a sharper you!"
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
